import { useMutation } from '@tanstack/react-query'
import { Camera, Crop as CropIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { readRef, type Crop } from '../../../shared/crop'
import { CropPhoto } from '@/components/CropPhoto'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { PHOTO_TYPES, saveCrop } from '@/lib/photos'
import { isFace, portraitUrl, uploadPortrait, type Person } from '@/lib/tree'

// A new portrait is cropped as it's added, square to start with since it mostly shows in a circle; the one there can
// be cropped again. Only the copies change: the original stays as it was added.
export function PortraitButton({ person, onDone }: { person: Person; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [recrop, setRecrop] = useState<{ id: string; crop: Crop | null } | null>(null)
  // A page showing its account's profile photo has no portrait of its own yet.
  const own = person.portrait && !isFace(person.portrait) ? person.portrait : null
  const open = useMutation({
    mutationFn: (ref: string) => api<{ id: string; crop: Crop | null }>(`/portraits/${readRef(ref).id}`),
    onSuccess: setRecrop,
  })
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={PHOTO_TYPES}
        className="sr-only"
        aria-label={`Photo of ${person.name}`}
        onChange={(e) => {
          const chosen = e.target.files?.[0]
          if (chosen) setFile(chosen)
          e.target.value = ''
        }}
      />
      <Button variant="outline" size="sm" onClick={() => input.current?.click()}>
        <Camera aria-hidden /> {own ? 'New photo' : 'Add a photo'}
      </Button>
      {own && (
        <Button variant="outline" size="sm" disabled={open.isPending} onClick={() => open.mutate(own)}>
          <CropIcon aria-hidden /> Rotate, crop and color
        </Button>
      )}
      {open.error && <FormError error={open.error} />}
      {file && (
        <CropPhoto
          open
          onOpenChange={(o) => !o && setFile(null)}
          source={file}
          crop={null}
          shape="square"
          adding
          onSave={async (crop, copies, size) => {
            await uploadPortrait(person.id, { original: file, ...copies, ...size, crop })
            onDone()
          }}
        />
      )}
      {recrop && (
        <CropPhoto
          open
          onOpenChange={(o) => !o && setRecrop(null)}
          source={portraitUrl(recrop.id, 'original')}
          crop={recrop.crop}
          onSave={async (crop, copies) => {
            await saveCrop(`/portraits/${recrop.id}`, crop, copies)
            onDone()
          }}
        />
      )}
    </>
  )
}
